import { Injectable } from "@angular/core";
import { AuthHttp } from "angular2-jwt";
import { Headers, ResponseContentType, RequestOptions } from "@angular/http";
import { Router } from "@angular/router";
import { environment } from "../environments/environment";
import { SessionStorageService } from "../services/session-storage.service";
import "rxjs/add/operator/toPromise";
import "rxjs/Rx";
import { PromiseObservable } from "rxjs/observable/PromiseObservable";
import { promise } from "protractor";

export abstract class APIBase {
  //Variáveis auxiliares
  private __url = environment.api;

  protected __entity = "";

  /**
   * construct
   *
   * Incorpora o AuthHttp
   *
   * @access public
   * @author Henrique de Castro
   * @since  01/2018
   * @return void
   */
  constructor(
    private authHttp: AuthHttp,
    private router: Router,
    public session: SessionStorageService
  ) {}

  /**
   * _call
   *
   * Realiza uma chamada a API
   *
   * @access protected
   * @author Henrique de Castro
   * @since  01/2018
   * @param  string
   * @param  object
   * @return object Promise
   */
  protected _call(method, body?, requestMethod = "post") {
    // Retorna a promessa
    return new Promise<any>((resolve, reject) => {
      // seta o usuário id
      let myHeader = new Headers();
      myHeader.append("user_id", this.session.getUserId());
      myHeader.append("group_id", this.session.getUserGroupId());

      // Monta os dados
      if (body && requestMethod != "get") body = JSON.stringify(body);
      let url = this.__url + "/" + method;
      let request: Promise<any>;

      // Analisa o método
      // Monta a requisição/url/params para enviar a api com base no method
      switch (requestMethod.toLowerCase()) {
        case "get":
          let conector = "?";
          for (let key in body) {
            url += conector + key + "=" + body[key];
            conector = "&";
          }
          request = this.authHttp.get(url, { headers: myHeader }).toPromise();
          break;

        case "delete":
          request = this.authHttp
            .delete(url, { headers: myHeader })
            .toPromise();
          break;

        case "put":
          request = this.authHttp
            .put(url, body, { headers: myHeader })
            .toPromise();
          break;

        case "post":
          request = this.authHttp
            .post(url, body, { headers: myHeader })
            .toPromise();
          break;

        default:
          reject("Método " + requestMethod + " inválido");
      }
      // Recebe o retorno da api e começa tratar
      request
        .then((success) => {
          this.__success(success).then(resolve).catch(reject);
        })
        .catch((error) => {
          this.__error(error).then(resolve).catch(reject);
        });
    });
  }

  /**
   * setForm
   *
   * Realiza a busca de um item especifico e traz os dados necessários para a edição ou criação
   *
   * @access public
   * @author Murilo Spinoza de Arruda
   * @since  01/2018
   * @param  string
   * @param  string
   * @return object Promise
   */
  public setForm(id) {
    return this._call(this.__entity + "/set_form", { id: id }, "get");
  }

  /**
   * save
   *
   * Salva os dados
   *
   * @access public
   * @author Murilo Spinoza de Arruda
   * @since  01/2018
   * @param  object
   * @return object Promise
   */
  public save(data) {
    return this._call(this.__entity, data, data.id ? "put" : "post");
  }

  /**
   * get
   *
   * get all dados
   *
   * @access public
   * @author Murilo Spinoza de Arruda
   * @since  01/2018
   * @param  object
   * @return object Promise
   */
  public get() {
    return this._call(this.__entity, {}, "get");
  }
  /**
   * get
   *
   * retorna relatorio do sistema completo
   *
   * @access public
   * @author Nicolas Aoki
   * @since  01/2020
   * @param  object
   * @return object Promise
   */
  public getBlob(method, params?) {
    let filter: string = "";

    if (params["days"]) {
      filter = "?day=" + params["days"];
    }
    if (params["calendar"]) {
      filter =
        "?inicio=" +
        params["calendar"].inicio +
        "&fim=" +
        params["calendar"].fim;
    }

    try {
      //Seta o cabecalho
      let myHeader = new Headers();
      myHeader.append("user_id", this.session.getUserId());
      myHeader.append("group_id", this.session.getUserGroupId());

      //nome do model para retirar o relatorio
      let url = this.__url + "/" + method;
      //chrome + IE padronizacao
      myHeader.append(
        "Content-Type",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );

      this.authHttp
        .get(url + filter, {
          headers: myHeader,
          responseType: ResponseContentType.Blob,
        })
        .subscribe((res: any) => {
          const file = new Blob([res._body], {
            type: res.type,
          });
          //Simula criacao de um elemento a
          const blob = window.URL.createObjectURL(file);
          const link = document.createElement("a");
          //referencia do blob
          link.href = blob;
          //nome do relatorio do backend #deve-se automatizar depois
          link.download = "report.csv";
          //Simula o click no elemento <a> criado
          link.click();
          //remove os objetos instaciados
          window.URL.revokeObjectURL(blob);
          link.remove();

          return true;
        });
    } catch (err) {
      return err;
    }
  }

  /**
   * __success
   *
   * Analisa o sucesso na requisição
   *
   * @access private
   * @author Henrique de Castro
   * @since  01/2018
   * @param  object
   * @return object Promise
   */
  private __success(res) {
    // Retorna a promessa
    return new Promise<any>((resolve, reject) => {
      // pega a resposta
      const body = res.json();

      // Verifica se retornou um erro ou nao
      if (body.status) resolve(body.data);
      else reject(body.error);
    });
  }

  /**
   * __error
   *
   * Analisa o erro na requisição
   *
   * @access private
   * @author Henrique de Castro
   * @since  01/2018
   * @param  object
   * @return object Promise
   */
  private __error(error) {
    // Retorna a promessa
    return new Promise<any>((resolve, reject) => {
      const errorMsg = error.message
        ? error.message
        : error.status
        ? error.status + "-" + error.statusText
        : "Erro na requisição";

      if (error.status == 401 && this.router) {
        alert("Sessão encerrada");
        this.router.navigate(["auth"]);
      }

      reject(errorMsg);
    });
  }
}



// WEBPACK FOOTER //
// ./src/api/api.base.ts